import { Dispatch, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import {
  IKnmErknmFormViolatedRequirementsMandatory,
  IKnmErknmFormViolatedRequirementsNpa,
} from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { DECISION_VIOLATIONS } from 'CorePackage/MainModule/routes/routes';
import { StructureRequirementsNpaModal } from 'KnmPackage/StructureRequirementsNpaModule/StructureRequirementsNpaModal';
import { IRequirementsNpaInitValues } from 'KnmPackage/RequirementsNpaModalModule/components/RequirementsNpaModal';
import { KnmActViolations } from 'KnmPackage/KnmPaperlessActModule/KnmActViolations/KnmActViolations';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IRequirementsNpaItem } from 'KnmPackage/RequirementsNpaModalModule/interfaces/IRequirementsNpaItem';
import { IDecisionActForm } from 'DecisionPackage/MainModule/interfaces/IDecisionActForm';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { WAS_REMOVE_DUPLICATE_MANDATORY, WAS_REMOVE_DUPLICATE_NPA } from 'CorePackage/MainModule/constants/notices';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';

import { IKnmErknmFormObject } from '../../../../KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';

interface IDecisionActViolationsProps {
  watchOnly: boolean;
  knoOrganization: IDictionaryKnoOrganizationSelectOption | undefined;
  supervisionType: IDictionarySupervisionTypeSelectOption | undefined;
  objects?: IKnmErknmFormObject[];
  id: string;
  isDisabledField: boolean;
  name: string;
  value: IDecisionActForm;
  isDisabledByDirty?: boolean;
  isEnablePrescriptionCheck: boolean;
  isPrescriptionCheck: boolean;
}

interface IDecisionActViolationsStoreProps {
  formRef: IDecisionFormRef | undefined;
}

const DecisionActViolationsWithoutStore = ({
  watchOnly,
  formRef,
  knoOrganization,
  supervisionType,
  objects,
  id,
  value,
  isDisabledField,
  name,
  isDisabledByDirty = false,
  isEnablePrescriptionCheck,
  isPrescriptionCheck,
}: IDecisionActViolationsProps & IDecisionActViolationsStoreProps) => {
  const requirementsFieldName = `${name}.violatedMandatoryRequirements`;
  const push = formRef?.current.mutators.push;
  const [isRequirementsModalVisible, setIsRequirementsModalVisible] = useState(false);
  const [initValues, setInitValues] = useState<IRequirementsNpaInitValues | undefined>(undefined);
  const [npaFieldName, setNpaFieldName] = useState('');
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const openRequirementsModal = () => {
    setInitValues(undefined);
    setIsRequirementsModalVisible(true);
  };

  const handleAddRequirementsNpa = (npaItems: IRequirementsNpaItem[]) => {
    const requirements = formRef?.current?.getFieldState(requirementsFieldName as keyof IDecisionForm)
      ?.value as IKnmErknmFormViolatedRequirementsNpa[];

    if (initValues && npaItems.length === 1) {
      let hasFoundDuplicate = false;

      if (push) {
        const npaIndex = requirements.findIndex(
          req => req.npa.id === initValues.npa.id && req.structuralUnit.id === initValues.structuralUnit.id,
        );

        if (npaIndex > -1) {
          npaItems[0].mandatory?.forEach(item => {
            const mandatoryIndex = requirements[npaIndex].violatedMandatory.findIndex(
              vm => vm.mandatory.id === item.id,
            );

            if (mandatoryIndex === -1) {
              const newMandatoryItem: IKnmErknmFormViolatedRequirementsMandatory = {
                mandatory: item,
                category: undefined,
                violations: null,
              };

              push(npaFieldName, newMandatoryItem);
            } else {
              hasFoundDuplicate = true;
            }
          });

          if (hasFoundDuplicate) {
            dispatch(
              addNoticeAction({
                renderContent: WAS_REMOVE_DUPLICATE_MANDATORY,
                noticeType: NoticeTypesEnum.ERROR,
                messageType: MessageTypesEnum.NPA_CHECK,
              }),
            );
          }
        }
      }
    } else {
      let hasFoundDuplicate = false;

      if (push) {
        npaItems.forEach(item => {
          const newNpaItem: IKnmErknmFormViolatedRequirementsNpa = {
            guid: null,
            isNew: true,
            npa: item.npa,
            structuralUnit: item.structuralUnit,
            violatedMandatory: item.mandatory.map(mandatoryItem => ({
              mandatory: mandatoryItem,
              category: undefined,
              violations: null,
            })),
          };

          if (requirements?.length) {
            const npaIndex = requirements.findIndex(
              req => req.npa.id === item.npa.id && req.structuralUnit.id === item.structuralUnit.id,
            );

            if (npaIndex > -1) {
              hasFoundDuplicate = true;
            } else {
              push(requirementsFieldName, newNpaItem);
            }
          } else {
            push(requirementsFieldName, newNpaItem);
          }
        });
      }

      if (hasFoundDuplicate) {
        dispatch(
          addNoticeAction({
            renderContent: WAS_REMOVE_DUPLICATE_NPA,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.NPA_CHECK,
          }),
        );
      }
    }
  };

  const addViolatedMandatory = (initialValue: IRequirementsNpaInitValues, fieldName: string) => {
    setInitValues(initialValue);
    setNpaFieldName(fieldName);
    setIsRequirementsModalVisible(true);
  };

  return (
    <div id={DECISION_VIOLATIONS}>
      <KnmActViolations
        addRequirementsNpa={openRequirementsModal}
        addViolatedMandatory={addViolatedMandatory}
        id={id}
        isWatchOnly={watchOnly}
        name={name}
        value={value}
        isDisabledField={isDisabledField}
        isDisabledByDirty={isDisabledByDirty}
        isDecision
        formRef={formRef}
        isPaperless={true}
        isEnablePrescriptionCheck={isEnablePrescriptionCheck}
        isPrescriptionCheck={isPrescriptionCheck}
      />
      {isRequirementsModalVisible && (
        <StructureRequirementsNpaModal
          onClose={() => setIsRequirementsModalVisible(false)}
          knoVersionId={knoOrganization?.value.dictVersionId || ''}
          supervisionVersionId={supervisionType?.value.dictVersionId || ''}
          withRequirements={true}
          addRequirementsNpa={handleAddRequirementsNpa}
          initValues={initValues}
          objects={objects}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: IRootState): IDecisionActViolationsStoreProps => ({
  formRef: getDecisionFormRef(state),
});

export const DecisionActViolations = connect(mapStateToProps)(DecisionActViolationsWithoutStore);
